import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  PDFViewer,
  type PDFViewerHandle,
  type PDFViewerPageOverlayProps
} from "@/components/extend/pdf-viewer";

export type CitationPageOverlayRenderArgs = {
  pageIndex: number;
  pageNumber: number;
  renderedPageWidth: number;
  renderedPageHeight: number;
};

type Props = {
  /** Blob or http URL of the cited document. */
  file: string;
  ariaLabel: string;
  /** 0-based page to open on. */
  scrollToPageIndex?: number | null;
  /** Where on that page to land, from 0 (top) to 1 (bottom). */
  scrollToPageOffsetRatio?: number | null;
  renderPageOverlay?: (args: CitationPageOverlayRenderArgs) => React.ReactNode;
  unavailableText?: string;
};

const JUMP_RETRY_MS = 100;
const JUMP_MAX_TRIES = 50;

/**
 * A cited document that opens on the cited line with the value highlighted.
 * Built on the Extend viewer because the highlight is drawn over the page,
 * which the Case IDE's EmbedPDF viewer has no hook for.
 */
export const CitationPdfViewer = ({
  file,
  ariaLabel,
  scrollToPageIndex = null,
  scrollToPageOffsetRatio = null,
  renderPageOverlay,
  unavailableText = "Preview unavailable."
}: Props) => {
  const viewerRef = useRef<PDFViewerHandle | null>(null);
  const [loadedFile, setLoadedFile] = useState<string | null>(null);
  const [failedFile, setFailedFile] = useState<string | null>(null);

  const overlayRef = useRef(renderPageOverlay);
  overlayRef.current = renderPageOverlay;
  const renderOverlay = useCallback(
    ({ pageNumber, pageWidth, pageHeight }: PDFViewerPageOverlayProps) =>
      overlayRef.current?.({
        pageIndex: pageNumber - 1,
        pageNumber,
        renderedPageWidth: pageWidth,
        renderedPageHeight: pageHeight
      }),
    []
  );

  const isLoaded = loadedFile === file;

  useEffect(() => {
    if (!isLoaded || scrollToPageIndex == null) return undefined;
    const pageNumber = scrollToPageIndex + 1;
    const ratio =
      scrollToPageOffsetRatio == null
        ? null
        : Math.max(0, Math.min(1, scrollToPageOffsetRatio));
    let tries = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // A jump made before the viewer lays out its first page is dropped.
    const attempt = () => {
      const viewer = viewerRef.current;
      const laidOut = viewer
        ?.getViewportElement()
        ?.querySelector("[data-pdf-viewer-page]");
      if (viewer && laidOut) {
        if (ratio == null) viewer.scrollToPage(pageNumber);
        else viewer.scrollToPageArea(pageNumber, { top: ratio * 100 });
        return;
      }
      tries += 1;
      if (tries < JUMP_MAX_TRIES) timer = setTimeout(attempt, JUMP_RETRY_MS);
    };
    attempt();
    return () => clearTimeout(timer);
  }, [isLoaded, scrollToPageIndex, scrollToPageOffsetRatio]);

  if (failedFile === file) {
    return (
      <div className="flex size-full items-center justify-center px-4 text-center">
        <p className="text-sm text-fg-3">{unavailableText}</p>
      </div>
    );
  }

  return (
    <div className="size-full min-h-0" role="region" aria-label={ariaLabel}>
      <PDFViewer
        ref={viewerRef}
        key={file}
        src={file}
        defaultZoom="fit-page"
        showUpload={false}
        showRotateControls={false}
        renderPageOverlay={renderPageOverlay ? renderOverlay : undefined}
        onDocumentLoadSuccess={() => setLoadedFile(file)}
        onDocumentLoadError={() => setFailedFile(file)}
      />
    </div>
  );
};
